import {
  emptyProductAttributeSortedSet,
  PriceRangeNullable,
  ProductAttributeSortedSet,
} from '@vs-shop/schemas/product/filter/backend/productFilters';
import { Effect, Schema } from 'effect';

import { overrideSchema } from '@vs-shop/schemas/openapi/overrideSchema';
// Reuse the existing `TaxonCode` value-object brand (WS7: never re-declare a
// brand). It is the same domain value as the filter-state taxon code.
import { TaxonCode } from '@vs-shop/schemas/product/filter/state/taxon';
// Branded banner element for the nested banners array (WS7 Task 7b).
import { Banner } from '@vs-shop/schemas/branded/MiscSchema';
// Branded related-article element for the nested articles array (WS7 Task 8).
import { ArticleIndexFavourite } from '@vs-shop/schemas/branded/ContentSchema';
import { BrandIndexDetail } from '@vs-shop/schemas/brand/brand';

const JsonLdContext = Schema.optionalKey(
  Schema.Union(
    [
      Schema.String,
      Schema.Struct({
        '@vocab': Schema.String,
        hydra: Schema.Literal('http://www.w3.org/ns/hydra/core#'),
      }),
    ],
    { mode: 'oneOf' }
  ).annotate({ readOnly: true })
);
const JsonLdIri = Schema.optionalKey(Schema.String.annotate({ readOnly: true }));
const JsonLdType = Schema.optionalKey(Schema.String.annotate({ readOnly: true }));
const NullableString = Schema.Union([Schema.String, Schema.Null]);

export const TaxonImageMenu = Schema.Struct({
  '@context': JsonLdContext,
  '@id': JsonLdIri,
  '@type': JsonLdType,
  pathAbstract: Schema.optionalKey(NullableString.annotate({ readOnly: true })),
});
export type TaxonImageMenu = Schema.Schema.Type<typeof TaxonImageMenu>;

const TaxonExpandedFields = {
  '@context': JsonLdContext,
  '@id': JsonLdIri,
  '@type': JsonLdType,
  listing: Schema.Literals(['ajax', 'inline']),
  visibility: Schema.Literals(['hideInMenu', 'hideCompletely', 'visible']),
  images: Schema.optionalKey(Schema.Array(TaxonImageMenu).annotate({ readOnly: true })),
  id: Schema.optionalKey(Schema.Finite.annotate({ readOnly: true }).check(Schema.isInt())),
  code: Schema.String,
  nameInTaxonList: NullableString,
  metaTitle: NullableString,
  metaDescription: NullableString,
  name: NullableString,
  slug: NullableString,
  description: NullableString,
  attributes: Schema.optionalKey(Schema.Array(Schema.Unknown).annotate({ readOnly: true })),
  priceRange: Schema.optionalKey(Schema.Unknown.annotate({ readOnly: true })),
  routerUrl: Schema.String.annotate({ readOnly: true }),
  banners: Schema.optionalKey(Schema.Array(Banner).annotate({ readOnly: true })),
  breadcrumbs: Schema.optionalKey(
    Schema.Array(
      Schema.Struct({
        '@id': JsonLdIri,
        '@type': JsonLdType,
        code: Schema.String,
        nameInTaxonList: NullableString,
        name: NullableString,
        routerUrl: Schema.String.annotate({ readOnly: true }),
      })
    ).annotate({ readOnly: true })
  ),
  articles: Schema.optionalKey(Schema.Array(ArticleIndexFavourite).annotate({ readOnly: true })),
  brands: Schema.optionalKey(Schema.Array(BrandIndexDetail).annotate({ readOnly: true })),
  totalProducts: Schema.optionalKey(Schema.Finite.check(Schema.isInt())),
};

const TaxonOverrides = {
  attributes: ProductAttributeSortedSet.pipe(
    Schema.withDecodingDefaultType(Effect.succeed(emptyProductAttributeSortedSet))
  ),
  priceRange: PriceRangeNullable.pipe(Schema.withDecodingDefaultType(Effect.succeed(null))),
  '@id': Schema.optionalKey(
    Schema.String.annotate({ readOnly: true }).pipe(Schema.brand('Taxon.iri'))
  ),
  code: TaxonCode,
  name: Schema.Union([Schema.String.pipe(Schema.brand('Taxon.name')), Schema.Null]),
  nameInTaxonList: Schema.Union([
    Schema.String.pipe(Schema.brand('Taxon.nameInTaxonList')),
    Schema.Null,
  ]),
  slug: Schema.Union([Schema.String.pipe(Schema.brand('Taxon.slug')), Schema.Null]),
  routerUrl: Schema.String.annotate({
    description: 'Frontend router URL for this resource.',
    readOnly: true,
  }).pipe(Schema.brand('Taxon.routerUrl')),
  banners: Schema.optionalKey(Schema.Array(Banner).annotate({ readOnly: true })),
  articles: Schema.optionalKey(Schema.Array(ArticleIndexFavourite).annotate({ readOnly: true })),
};

const TaxonExpandedChild = overrideSchema(
  Schema.Struct(TaxonExpandedFields),
  TaxonOverrides
).annotate({ identifier: 'TaxonExpandedChild' });

const TaxonExpandedBase = Schema.Struct({
  ...TaxonExpandedFields,
  children: Schema.optionalKey(
    Schema.Array(TaxonExpandedChild).annotate({
      description: 'First level = Taxon object, deeper levels are supplied by the endpoint.',
      readOnly: true,
    })
  ),
});

// `overrideSchema` returns a plain `Schema.Struct({...})` (no `StructWithRest`).
// This side-steps the Effect v4 `StructWithRest` decode-transform bug AND
// pins the override key set against the generated schema's at the type
// level — see the sibling `taxon.tst.ts` for the drift assertions.
//
// WS7 branded core contract: each branded field wraps the field's EXACT
// generated schema (Encoded unchanged, decoded value byte-identical). The
// `@id` IRI carries a plain-string `Taxon.iri` brand — NOT `iri.ts`'s
// `TaxonIRI` parser brand, which decodes the string into a tuple (a runtime
// change). `code` reuses the `TaxonCode` value-object brand.
export const TaxonExpandedResponse = overrideSchema(TaxonExpandedBase, TaxonOverrides).annotate({
  identifier: 'TaxonExpandedResponse',
});

export type TaxonExpandedResponse = Schema.Schema.Type<typeof TaxonExpandedResponse>;
